import { cloneElement, useId, type ReactElement } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ViewerCapabilities } from "@constructionautomation/backend/convex/lib/viewerCapabilities";
import { READ_ONLY_REASON } from "./read-only-copy";
import { useViewerCapabilities } from "./use-viewer-capabilities";

export type Capability = Exclude<keyof ViewerCapabilities, "requiresProjectAssignment">;

type ControlProps = {
  disabled?: boolean;
  onClick?: unknown;
  "aria-describedby"?: string;
};

/**
 * Gate one control on a viewer capability. `hide` removes controls that would only be noise;
 * `disable` keeps controls users expect to find, but unfocusable and with an accessible reason
 * (aria-disabled plus a description, and a hover tooltip). Not for menu items: hide those instead.
 */
export function RequiresCapability({
  capability = "canEditWork",
  mode,
  reason = READ_ONLY_REASON,
  children,
}: {
  capability?: Capability;
  mode: "hide" | "disable";
  reason?: string;
  children: ReactElement<ControlProps>;
}) {
  const allowed = useViewerCapabilities()?.[capability] ?? false;
  const descriptionId = useId();
  if (allowed) return children;
  if (mode === "hide") return null;

  const describedBy = [children.props["aria-describedby"], descriptionId].filter(Boolean).join(" ");
  const control = cloneElement(children, {
    disabled: true,
    "aria-disabled": true,
    "aria-describedby": describedBy,
    onClick: undefined,
  } as ControlProps);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Disabled controls swallow pointer events, so the wrapper owns the hover. */}
        <span data-slot="requires-capability" className="inline-flex cursor-not-allowed">
          {control}
          <span id={descriptionId} className="sr-only">
            {reason}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}
